<script setup lang="ts">
import type { CommonHeaderProperties } from '@optionway/models';
import { Menu, TicketsPlane, X } from 'lucide-vue-next';

const properties = defineProps<CommonHeaderProperties>();

const { $appConfig } = useNuxtApp();
const { openBeacon } = useHelpscoutBeacon();
const gtmStore = useGtmStore();
const route = useRoute();

const prefix = 'components.common.header';
const config = $appConfig.app?.components?.commonHeader;
const isBurgerMenuOpen = ref(false);

const hasBottomShadow = computed(() => String(route.name ?? '').includes('confirmation'));

const componentConfig = computed<CommonHeaderProperties>(() => {
  return {
    logoAlt: properties.logoAlt ?? config?.logoAlt ?? 'Option Way',
    logoName: properties.logoName ?? config?.logoName ?? 'optionway',
    helpUrl: properties.helpUrl ?? config?.helpUrl,
    components: properties.components ?? config?.components,
    hideComponents: properties.hideComponents ?? config?.hideComponents,
  };
});

const localizedHelpUrl = useLocalized(componentConfig.value.helpUrl);

const showLocaleSelector = computed(
  () => !componentConfig.value.hideComponents?.commonLocaleSelector && !route.meta.hideLocaleSelector,
);

const contactSupport = () => {
  gtmStore.trackContactOpened();
  openBeacon();
};
</script>

<template>
  <header :class="['mx-auto w-full px-4 py-3 md:px-6 lg:px-8', { 'shadow-sm': hasBottomShadow }]">
    <div class="mx-auto flex max-w-container items-center justify-between gap-4">
      <!-- Logo: always visible -->
      <NuxtLinkLocale
        to="index"
        class="shrink-0 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-primary-700"
      >
        <component
          :is="`SvgoCommonHeaderPartner${useUpperFirst(componentConfig.logoName ?? 'optionway')}`"
          filled
          :aria-label="componentConfig.logoAlt"
          width="139"
          height="29"
          :font-controlled="false"
        />
      </NuxtLinkLocale>

      <div class="hidden items-center gap-2 lg:flex lg:flex-1 lg:justify-end xl:gap-4">
        <UiButton
          as-child
          variant="tertiary"
          color="gray"
          class="px-2 py-2.5 xl:px-3"
        >
          <NuxtLink
            :to="localizedHelpUrl"
            target="_blank"
            :title="$t(`${prefix}.helpTitle`)"
          >
            {{ $t(`${prefix}.help`) }}
          </NuxtLink>
        </UiButton>
        <UiButton
          variant="tertiary"
          color="gray"
          class="px-2 py-2.5 xl:px-3"
          @click="contactSupport"
        >
          {{ $t(`${prefix}.contact`) }}
        </UiButton>
        <CommonCurrencySelector
          v-if="!componentConfig.hideComponents?.commonCurrencySelector"
          :currencies="componentConfig.components?.commonCurrencySelector?.currencies"
        />
        <CommonLocaleSelector
          v-if="showLocaleSelector"
          :languages="componentConfig.components?.commonLocaleSelector?.languages"
        />
      </div>

      <!-- Right: My Booking + Burger -->
      <div class="ml-auto flex shrink-0 items-center gap-2">
        <UiButton
          as-child
          variant="secondary"
          color="brand"
          size="sm"
          class="lg:px-3.5 lg:py-2.5"
        >
          <NuxtLinkLocale
            to="my-booking"
            :aria-label="$t(`${prefix}.myBooking`)"
          >
            <TicketsPlane
              class="text-brand-primary-700"
              :stroke-width="1.5"
              aria-hidden="true"
            />
            <span class="hidden text-sm font-semibold text-brand-primary-700 lg:inline">{{
              $t(`${prefix}.myBooking`)
            }}</span>
          </NuxtLinkLocale>
        </UiButton>

        <!-- Hamburger: mobile only -->
        <UiSheet v-model:open="isBurgerMenuOpen">
          <!-- as-child: the trigger must merge into UiButton's own <button> — a wrapping trigger
               button would nest <button> in <button>, invalid HTML the browser parser splits,
               which desynchronizes hydration. -->
          <UiSheetTrigger as-child>
            <UiButton
              class="bg-transparent p-2.5 shadow-none hover:bg-transparent focus-visible:border-brand-primary-700 lg:hidden"
              :aria-label="$t(`${prefix}.openMenu`)"
            >
              <Menu class="size-5 text-secondary-foreground" />
            </UiButton>
          </UiSheetTrigger>

          <UiSheetContent
            side="right"
            class="w-full p-0 lg:w-80"
            hide-close
          >
            <div class="inset-0 flex flex-col gap-1 p-4 lg:hidden">
              <div class="flex items-center justify-between">
                <NuxtLinkLocale
                  to="index"
                  class="ml-2 shrink-0 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-primary-700"
                >
                  <component
                    :is="`SvgoCommonHeaderPartner${useUpperFirst(componentConfig.logoName ?? 'optionway')}`"
                    filled
                    :aria-label="componentConfig.logoAlt"
                    width="139"
                    height="29"
                    :font-controlled="false"
                  />
                </NuxtLinkLocale>
                <UiSheetClose as-child>
                  <UiButton
                    variant="tertiary"
                    color="gray"
                    class="p-3 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-primary-700"
                    :aria-label="$t(`${prefix}.closeMenu`)"
                  >
                    <X class="size-6 text-secondary-foreground" />
                  </UiButton>
                </UiSheetClose>
              </div>

              <nav class="mt-5 flex flex-col items-stretch gap-2">
                <CommonLocaleSelector
                  v-if="showLocaleSelector"
                  :languages="componentConfig.components?.commonLocaleSelector?.languages"
                />
                <CommonCurrencySelector
                  v-if="!componentConfig.hideComponents?.commonCurrencySelector"
                  :currencies="componentConfig.components?.commonCurrencySelector?.currencies"
                />
                <UiButton
                  as-child
                  variant="tertiary"
                  color="gray"
                  class="flex w-full justify-start px-3 py-3 text-sm font-normal text-text-secondary-600 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-primary-700"
                >
                  <NuxtLink
                    :to="localizedHelpUrl"
                    target="_blank"
                    :title="$t(`${prefix}.helpTitle`)"
                  >
                    {{ $t(`${prefix}.help`) }}
                  </NuxtLink>
                </UiButton>
                <UiSheetClose as-child>
                  <UiButton
                    variant="tertiary"
                    color="gray"
                    class="flex w-full justify-start px-3 py-2.5 text-sm font-normal text-text-secondary-600 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-primary-700"
                    @click="contactSupport"
                  >
                    {{ $t(`${prefix}.contact`) }}
                  </UiButton>
                </UiSheetClose>
                <UiButton
                  as-child
                  variant="secondary"
                  color="brand"
                  size="sm"
                  class="flex w-full"
                >
                  <NuxtLinkLocale
                    to="my-booking"
                    @click="isBurgerMenuOpen = false"
                  >
                    <TicketsPlane
                      class="text-brand-primary-700"
                      :stroke-width="1.5"
                    />
                    <span class="text-sm font-semibold text-brand-primary-700">{{ $t(`${prefix}.myBooking`) }}</span>
                  </NuxtLinkLocale>
                </UiButton>
              </nav>
            </div>
          </UiSheetContent>
        </UiSheet>
      </div>
    </div>
  </header>
</template>
